<script setup lang="ts">
import { FilterTypes, RestaurantLiteWithDistanceList } from '~/lib/interfaces/restaurant'
import { GTMViewContentGroup, GTMContentGroup } from '~/lib/models/gtm'

const { t } = useLocale()

const props = withDefaults(
  defineProps<{
    ecommerce?: boolean
    mdwRestuarantList?: RestaurantLiteWithDistanceList
    mapCenter?: { lat: number; lng: number }
    onBack?: boolean
  }>(),
  {
    ecommerce: true,
    mdwRestuarantList: undefined,
    mapCenter: undefined,
  }
)

const emit = defineEmits(['back'])

const restaurantStore = useRestaurantStore()
const { list: restauranListMop, filterRestaurants } = useRestaurantsMOP()
const { trackPageView, trackOnCloseModal, trackDisplayMapList, trackSelectFilter } = useGTM()

const isMobile = useIsMobile()
const showMap = ref(true)
const selectedLocation = restaurantStore.selectedGoogleLocation!

const searchFilters = ref([
  { text: FilterTypes.DRIVE_THRU, state: false },
  { text: FilterTypes.OPEN_NOW, state: false },
  { text: FilterTypes.MCCAFE, state: false },
])

const toggleFilter = (filterText: string) => {
  const filter = searchFilters.value.find(f => f.text === filterText)
  if (filter) {
    if (!filter.state) trackSelectFilter(filter.text)
    filter.state = !filter.state
  }
  filterRestaurants(
    searchFilters.value.filter(filter => filter.state).map(activeFilter => activeFilter.text)
  )
}

const restaurantList = computed(() => props.mdwRestuarantList ?? restauranListMop.value)
const pageViewPrimaryContent = computed(() => {
  return props.ecommerce ? GTMContentGroup.primary.ECOMMERCE : GTMContentGroup.primary.OUR_MENU
})

const toggleShowMap = () => {
  trackDisplayMapList(!showMap.value)
  showMap.value = !showMap.value
  trackPageView(
    {
      cg: pageViewPrimaryContent.value,
      cg2: GTMContentGroup.secondary.LOCATOR,
      cg3: showMap.value
        ? GTMContentGroup.tertiary.RESTAURANT_MAP
        : GTMContentGroup.tertiary.RESTAURANT_LIST,
    },
    true
  )
}

const handleClose = () => {
  if (props.onBack) {
    emit('back')
  } else {
    restaurantStore.closeModal()
    trackOnCloseModal()
  }
}

onMounted(() => {
  const viewContent: GTMViewContentGroup = {
    cg: pageViewPrimaryContent.value,
    cg2: GTMContentGroup.secondary.LOCATOR,
    cg3: GTMContentGroup.tertiary.RESTAURANT_MAP,
  }
  trackPageView(viewContent, true)
})
</script>

<template>
  <ModalCenter :add-close-button="false" :content-full="true">
    <template #header>
      <div class="flex items-center gap-2">
        <img
          class="flex-start h-8 cursor-pointer md:h-6"
          src="/images/icons/arrowLeft.svg"
          alt="close"
          @click.self.stop="handleClose"
        />

        <p class="md:hidden">{{ t('selectRestaurant.MOP.findYourMcDonalds') }}</p>
        <img
          class="flex-start ml-auto h-8 cursor-pointer md:hidden"
          :src="showMap ? '/images/icons/listView.svg' : '/images/icons/location.svg'"
          :alt="showMap ? 'listView' : 'location'"
          @click.self.stop="toggleShowMap"
        />
      </div>
    </template>
    <template #content>
      <div class="md:flex md:flex-col md:gap-4 md:px-14 md:py-8">
        <div class="hidden justify-between md:flex">
          <h2 class="text-2xl font-bold">{{ t('selectRestaurant.MOP.findYourMcDonalds') }}</h2>
          <McdButton
            class="h-10 w-[120px] text-sm"
            data-test="btn-toggle-map"
            @on-click="toggleShowMap"
          >
            {{ showMap ? t('selectRestaurant.MOP.seeList') : t('selectRestaurant.MOP.seeMap') }}
          </McdButton>
        </div>
        <RestaurantFilters
          :filters="searchFilters"
          :class="{ 'top-13 !absolute left-0 z-10': isMobile && showMap }"
          @toggle-filter="toggleFilter"
        />
      </div>
      <div
        class="relative w-full flex-1"
        :class="{ 'mcd-custom-scrollbar overflow-y-auto': !showMap }"
      >
        <MapGoogle
          v-show="showMap"
          :selected-location="selectedLocation"
          :restaurant-list="restaurantList"
          :center-map="mapCenter"
        />
        <div class="md:px-14">
          <RestaurantList
            v-if="!showMap"
            :small-card="isMobile"
            :restaurant-list="restaurantList"
            @after-select-restaurant="trackOnCloseModal"
          />
        </div>
      </div>
    </template>
  </ModalCenter>
</template>
